import { AudioSimplePlayer } from "@/component/AudioSimplePlayer";
import { VideoPreview } from "@/component/VideoPreview";
import { Attachment } from "@/types/attachment";
import { inferMediaKind } from "@/util/file";
import { CloseOutlined, FileOutlined } from "@ant-design/icons";
import { Image } from "antd";
import { convertFilePathToUrl } from "../../util/convertFilePathToUrl";
import styles from "./index.module.css";

type ChatBoxFileSize = "small" | "large";

interface IProps {
  attachmentList: Attachment[];
  onRemove?: (file: Attachment) => void;
  size?: ChatBoxFileSize; // small: 48px radius 8; large: 80px radius 16
  disabled?: boolean;
}

export function ChatBoxFiles(props: IProps) {
  const { attachmentList, onRemove, size = "large", disabled = false } = props;
  if (attachmentList.length === 0) {
    return null;
  }

  const isSmall = size === "small";
  const itemSize = isSmall ? 48 : 60;
  const borderRadius = isSmall ? 8 : 12;
  const closeButtonSize = isSmall ? 20 : 28;

  return (
    <div className="flex gap-x-3 gap-y-2 flex-wrap">
      {attachmentList.map((item, index) => {
        const mediaKind = inferMediaKind(item);
        const isVideo = mediaKind === "video";
        const isImage = mediaKind === "image";
        const isAudio = mediaKind === "audio";
        const previewSrc =
          item.type === "file"
            ? item.preview
            : convertFilePathToUrl(item.preview || item.url, {
                level: "medium",
              });

        if (!previewSrc) {
          return null;
        }

        return (
          <div
            key={item.id}
            className={`${styles.fileItem}`}
            style={{
              width: `${itemSize}px`,
              height: `${itemSize}px`,
              borderRadius: `${borderRadius}px`,
            }}
          >
            {isVideo && (
              <VideoPreview
                src={previewSrc}
                width={itemSize}
                height={itemSize}
              />
            )}
            {isImage && (
              <Image
                src={previewSrc}
                alt={`File ${index + 1}`}
                className="w-full h-full object-cover cursor-pointer"
                width={itemSize}
                height={itemSize}
                preview={{
                  mask: null,
                  src: item.type === "file" ? item.preview : item.url || "",
                }}
              />
            )}
            {isAudio && (
              <div
                style={{
                  width: `${itemSize}px`,
                  height: `${itemSize}px`,
                }}
              >
                <AudioSimplePlayer src={previewSrc} />
              </div>
            )}
            {!isVideo && !isImage && !isAudio && (
              <div
                className="flex items-center justify-center"
                style={{
                  width: `${itemSize}px`,
                  height: `${itemSize}px`,
                }}
              >
                <FileOutlined />
              </div>
            )}
            {!disabled && (
              <div
                className={styles.closeButtonWrapper}
                style={{
                  width: `${closeButtonSize}px`,
                  height: `${closeButtonSize}px`,
                }}
              >
                <button
                  type="button"
                  onClick={() => onRemove?.(item)}
                  className="cursor-pointer"
                >
                  <CloseOutlined
                    style={{
                      width: `${closeButtonSize / 2}px`,
                      height: `${closeButtonSize / 2}px`,
                    }}
                  />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
